import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Countdown from "./Countdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POUCH = "/assets/pouch.png";
const PLEXUS = "/assets/plexus-1.png";

export default function Hero({ onNotify }) {
  const pouchRef = useRef(null);
  const [time, setTime] = useState("");
  const [count, setCount] = useState(null);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      // Only enable parallax on large screens where the pouch is right-aligned;
      // on mobile/iPad portrait it sits below text and parallax pushes it into
      // the marquee/sections below.
      if (window.innerWidth < 1024) {
        if (pouchRef.current) pouchRef.current.style.transform = "";
        return;
      }
      const y = window.scrollY;
      if (pouchRef.current) {
        pouchRef.current.style.transform = `translateY(${y * 0.18}px) rotate(${y * 0.03}deg)`;
      }
    };
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    const fmt = () => {
      const d = new Date();
      const t = d.toLocaleTimeString("en-US", { hour12: false });
      setTime(t);
    };
    fmt();
    const id = setInterval(fmt, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/subscribers/count`)
      .then((r) => {
        if (!mounted) return;
        setCount(Math.max(0, parseInt(r.data?.count ?? 0, 10)));
      })
      .catch(() => setCount(0));
    return () => {
      mounted = false;
    };
  }, []);

  // animate count up when the value arrives
  useEffect(() => {
    if (count == null) return;
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(count * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [count]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-start lg:items-end overflow-hidden pt-20 lg:pt-40 pb-8 lg:pb-0">
      <img
        src={PLEXUS}
        alt=""
        aria-hidden
        className="hidden lg:block pointer-events-none absolute top-0 right-0 w-[50%] max-w-[900px] opacity-30 mix-blend-screen"
        style={{ maskImage: "linear-gradient(to left, #000 60%, transparent)" }}
      />

      {/* Top status bar — desktop only (on mobile it competes with the product) */}
      <div className="hidden lg:block absolute top-24 left-0 right-0 z-10">
        <div className="container-x px-6 lg:px-10 flex items-center justify-between font-mono text-[0.7rem] uppercase tracking-[0.25em] text-white/45">
          <div className="flex items-center gap-3">
            <span className="tag tag-blue">
              <span className="tag-dot" />
              Coming Soon · Performance Chew
            </span>
            <Countdown />
          </div>
          <div className="hidden md:flex items-center gap-6">
            <span>SYS // {time}</span>
            <span>Batch 001 / Eucalyptus Mint</span>
          </div>
        </div>
      </div>

      {/* Hero content */}
      <div className="container-x relative z-10 px-5 lg:px-10 pb-8 lg:pb-32 w-full">
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-10 items-center lg:items-end">
          {/* Pouch — appears FIRST on mobile, right column on desktop */}
          <div className="order-1 lg:order-2 lg:col-span-4 flex justify-center lg:justify-end w-full">
            <div ref={pouchRef} className="relative">
              <img
                src={POUCH}
                alt="Protos performance chew pouch"
                data-testid="hero-pouch"
                className="float-pouch w-[170px] sm:w-[240px] md:w-[300px] lg:w-[420px] xl:w-[500px] h-auto"
              />
              <div
                className="absolute -inset-10 -z-10 blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle at 50% 50%, rgba(31,179,255,0.35), transparent 60%)",
                }}
              />
            </div>
          </div>

          {/* Text content — second on mobile, left column on desktop */}
          <div className="order-2 lg:order-1 lg:col-span-8 text-center lg:text-left w-full">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-3 lg:mb-8">
              <div className="hidden lg:block h-px w-12 bg-white/40" />
              <span className="font-mono text-[0.6rem] lg:text-xs uppercase tracking-[0.3em] text-white/60">
                Focus · Calm · Control
              </span>
            </div>

            <h1 className="kinetic" style={{ fontSize: "clamp(2rem, 8.5vw, 7.5rem)" }}>
              <span className="block gradient-text">BEFORE THE</span>
              <span className="block outline-text-blue">MOMENTS</span>
              <span className="block">
                <span className="gradient-text">THAT MATTER</span>
                <span style={{ color: "var(--blue-bright)" }}>.</span>
              </span>
            </h1>

            <p className="mt-3 lg:mt-8 max-w-md mx-auto lg:mx-0 text-white/75 text-[0.82rem] sm:text-base lg:text-lg leading-relaxed">
              A performance chew engineered for focus, composure, and intentional state control.{" "}
              <span className="text-white">For the moments that matter most.</span>
            </p>

            {/* Mobile countdown */}
            <div className="lg:hidden mt-4 flex justify-center">
              <Countdown compact testId="countdown-mobile" />
            </div>

            <div className="mt-4 lg:mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-4">
              <button data-testid="hero-notify-btn" data-magnet onClick={onNotify} className="btn-primary">
                <span>Apply to the Protos 100</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-10 lg:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-10 border-t border-white/5 pt-6 lg:pt-8">
          {[
            ["00", "Sugar"],
            ["50", "Pieces / pouch"],
            ["90s", "To activate"],
          ].map(([n, l], i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="count-up">{n}</div>
              <div className="font-mono text-[0.7rem] uppercase tracking-[0.25em] text-white/50 mt-2">
                {l}
              </div>
            </div>
          ))}

          {/* Live waitlist counter */}
          <div data-testid="hero-stat-waitlist" className="min-w-0">
            <div
              className="count-up tabular-nums"
              data-testid="waitlist-count"
            >
              {count == null ? "—" : String(shown).padStart(2, "0")}
            </div>
            <div className="font-mono text-[0.7rem] uppercase tracking-[0.25em] mt-2 flex items-center gap-2 whitespace-nowrap">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                style={{
                  background: "var(--blue-bright)",
                  boxShadow: "0 0 8px var(--blue-bright)",
                  animation: "pulse 1.8s ease-in-out infinite",
                }}
              />
              <span style={{ color: "var(--blue-bright)" }}>
                of 100 · Claimed
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
